import { makeStyles } from '@material-ui/core';

const databaseStyles = makeStyles((theme) => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 14,
  },
  heroCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 30,
    padding: '24px 24px 22px',
    border: '1px solid #dbe6f3',
    background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 52%, #ecfeff 100%)',
    boxShadow: '0 24px 52px rgba(15, 23, 42, 0.08)',
    marginBottom: 18,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -90,
      right: -70,
      width: 220,
      height: 220,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(56,189,248,0.22) 0%, rgba(56,189,248,0) 72%)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -80,
      left: -60,
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0) 70%)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '20px 18px',
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.35fr) minmax(300px, 1fr)',
    gap: 18,
    alignItems: 'stretch',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  heroCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxWidth: 680,
  },
  heroEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#4b6488',
  },
  heroTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.08,
    color: '#0f172a',
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  heroText: {
    fontSize: 14,
    lineHeight: 1.65,
    color: '#48627f',
    maxWidth: 560,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
    alignSelf: 'stretch',
    marginBottom: 18,
  },
  summaryCard: {
    borderRadius: 18,
    border: '1px solid rgba(148,163,184,.24)',
    background: 'rgba(255,255,255,.82)',
    padding: '14px 14px 12px',
    minHeight: 96,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.92)',
  },
  summaryButton: {
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': { borderColor: '#173d61', backgroundColor: '#f6f9fc' },
    '&:focus-visible': { outline: '2px solid #d5ad3f', outlineOffset: 2 },
  },
  summaryActive: {
    borderColor: '#d5ad3f',
    boxShadow: '0 0 0 2px rgba(213,173,63,.16)',
  },
  summaryLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
    color: '#64748b',
    marginBottom: 10,
  },
  summaryValue: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#0f172a',
    marginBottom: 8,
  },
  summaryHint: {
    fontSize: 12,
    lineHeight: 1.45,
    color: '#48627f',
  },
  sectionLabel: {
    fontSize: 11,
    color: '#4b6488',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: 10,
    fontWeight: 700,
  },
  cardContainer: {
    height: '100%',
    borderRadius: 24,
    border: '1px solid #dbe6f3',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
    padding: 18,
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.07)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform .22s ease, box-shadow .22s ease, border-color .22s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '0 0 auto 0',
      height: 4,
      background: 'linear-gradient(90deg, #38bdf8 0%, #2563eb 100%)',
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 28px 54px rgba(15, 23, 42, 0.11)',
      borderColor: '#c7ddf5',
    },
  },
  cardPending: {
    '&::before': {
      background: 'linear-gradient(90deg, #f59e0b 0%, #f97316 100%)',
    },
  },
  cardHeader: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  cardTitleBlock: {
    minWidth: 0,
  },
  cardOverline: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#4b6488',
    fontWeight: 700,
    marginBottom: 6,
  },
  cardTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.08,
    color: '#0f172a',
    wordBreak: 'break-word',
  },
  cardCode: {
    marginTop: 8,
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    borderRadius: 999,
    padding: '5px 10px',
    background: 'rgba(79, 142, 247, 0.10)',
    color: '#2563eb',
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "'DM Mono', monospace",
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusPill: {
    borderRadius: 999,
    padding: '7px 12px',
    fontSize: 11,
    fontWeight: 700,
    border: '1px solid',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  statusSynced: {
    background: 'rgba(52,211,153,.12)',
    borderColor: 'rgba(52,211,153,.34)',
    color: '#047857',
  },
  statusPending: {
    background: 'rgba(251,191,36,.14)',
    borderColor: 'rgba(251,191,36,.3)',
    color: '#b45309',
  },
  fileBlock: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 16,
    padding: '12px 14px',
    border: '1px solid #e2ebf5',
    background: 'rgba(255,255,255,.82)',
    marginBottom: 14,
  },
  fileBlockButton: {
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color .2s ease, box-shadow .2s ease, transform .2s ease',
    '&:hover': {
      borderColor: '#bfdbfe',
      boxShadow: '0 12px 24px rgba(37, 99, 235, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  fileBlockDisabled: {
    cursor: 'not-allowed',
    opacity: 0.64,
    '&:hover': {
      borderColor: '#e2ebf5',
      boxShadow: 'none',
      transform: 'none',
    },
  },
  fileIcon: {
    width: 34,
    height: 34,
    flexShrink: 0,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
    color: '#ffffff',
    fontSize: 16,
  },
  fileMeta: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  fileLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#64748b',
    fontWeight: 700,
  },
  fileName: {
    fontSize: 13,
    lineHeight: 1.45,
    color: '#0f172a',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileHint: {
    fontSize: 11,
    lineHeight: 1.35,
    color: '#2563eb',
    fontWeight: 600,
  },
  fileActionIcon: {
    marginLeft: 'auto',
    color: '#2563eb',
    flexShrink: 0,
  },
  metaGrid: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px 12px',
    marginBottom: 16,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  metaItem: {
    borderRadius: 16,
    padding: '12px 13px',
    border: '1px solid #edf2f9',
    background: 'rgba(248,251,255,.92)',
  },
  metaLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
    color: '#64748b',
    fontWeight: 700,
    marginBottom: 6,
  },
  metaValue: {
    fontSize: 13,
    lineHeight: 1.45,
    color: '#1e293b',
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  mutedValue: {
    color: '#64748b',
  },
  metricsGrid: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },
  metricCard: {
    borderRadius: 18,
    padding: '14px 12px',
    background: '#ffffff',
    border: '1px solid #e2ebf5',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.92)',
  },
  metricValue: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#0f172a',
    marginBottom: 6,
  },
  metricLabel: {
    fontSize: 11,
    lineHeight: 1.35,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.7px',
  },
  actionRow: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 16,
  },
  actionButton: {
    flex: '1 1 150px',
    borderRadius: '14px !important',
    textTransform: 'none !important',
    fontWeight: '700 !important',
    padding: '10px 14px !important',
    boxShadow: 'none !important',
    border: '1px solid transparent !important',
  },
  actionButtonPrimary: {
    background: '#2563eb !important',
    color: '#ffffff !important',
    '&:hover': {
      background: '#1d4ed8 !important',
    },
    '&.Mui-disabled': {
      background: '#cbd5e1 !important',
      color: '#ffffff !important',
    },
  },
  actionButtonMuted: {
    background: '#e2e8f0 !important',
    color: '#64748b !important',
    borderColor: '#e2e8f0 !important',
  },
  actionButtonDanger: {
    background: 'rgba(248,113,113,.10) !important',
    color: '#b91c1c !important',
    borderColor: 'rgba(248,113,113,.26) !important',
    '&:hover': {
      background: 'rgba(248,113,113,.16) !important',
    },
  },
  stateCard: {
    borderRadius: 24,
    border: '1px dashed #bfd3ea',
    padding: '28px 22px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
    textAlign: 'center',
    color: '#48627f',
    boxShadow: '0 18px 38px rgba(15, 23, 42, 0.05)',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#2563eb',
    fontWeight: 600,
    marginBottom: 12,
  },
  stateTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 24,
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 8px',
  },
  stateText: {
    maxWidth: 560,
    margin: '0 auto',
    fontSize: 14,
    lineHeight: 1.65,
  },
}));

export default databaseStyles;
